/**
 * ErrorNotificationService.ts
 * Service unifié pour la gestion et la notification des erreurs
 * Phase 2.5 : Amélioration de la gestion d'erreurs
 */
import { ErrorHandlerService } from './ErrorHandlerService';
import { logger } from '../utils/ProfessionalLogger';

export type ErrorSeverity = 'low' | 'medium' | 'high' | 'critical';
export type ErrorCategory = 'network' | 'validation' | 'permission' | 'system' | 'user' | 'unknown';

export interface ErrorNotification {
  id: string;
  message: string;
  severity: ErrorSeverity;
  category: ErrorCategory;
  timestamp: Date;
  context?: Record<string, unknown>;
  retryable?: boolean;
  retryAction?: () => Promise<void>;
}

export interface ErrorFallback {
  message: string;
  action?: {
    label: string;
    handler: () => void;
  };
}

/**
 * Service unifié pour la gestion des erreurs et notifications
 */
export class ErrorNotificationService {
  private static instance: ErrorNotificationService | null = null;
  private errorHandler: ErrorHandlerService;
  private notifications: ErrorNotification[] = [];
  private maxNotifications = 50;
  private toastCallback?: (notification: ErrorNotification) => void;

  private constructor() {
    this.errorHandler = ErrorHandlerService.getInstance();
    this.setupGlobalErrorHandlers();
  }

  public static getInstance(): ErrorNotificationService {
    if (!ErrorNotificationService.instance) {
      ErrorNotificationService.instance = new ErrorNotificationService();
    }
    return ErrorNotificationService.instance;
  }

  /**
   * Configure le callback pour afficher les toasts
   */
  public setToastCallback(callback: (notification: ErrorNotification) => void): void {
    this.toastCallback = callback;
  }

  /**
   * Configure les gestionnaires d'erreurs globaux
   */
  private setupGlobalErrorHandlers(): void {
    // Erreurs non gérées
    window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
      const error =
        event.reason instanceof Error
          ? event.reason
          : new Error(String(event.reason || 'Promise rejetée'));

      this.notifyError(error, {
        category: 'system',
        severity: 'high',
        context: { type: 'unhandledRejection' },
      });
    });

    // Erreurs JavaScript
    window.addEventListener('error', (event: ErrorEvent) => {
      // Ignorer les erreurs de scripts externes
      if (event.filename && !event.filename.includes(window.location.origin)) {
        return;
      }

      const error = new Error(event.message || 'Erreur JavaScript');
      error.stack = event.error?.stack;

      this.notifyError(error, {
        category: 'system',
        severity: 'high',
        context: {
          type: 'javascriptError',
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
        },
      });
    });
  }

  /**
   * Notifie une erreur avec gestion automatique
   */
  public notifyError(
    error: Error | unknown,
    options: {
      severity?: ErrorSeverity;
      category?: ErrorCategory;
      context?: Record<string, unknown>;
      retryable?: boolean;
      retryAction?: () => Promise<void>;
      silent?: boolean;
    } = {}
  ): void {
    const errorMessage = this.extractErrorMessage(error);
    const severity = options.severity || this.determineSeverity(error);
    const category = options.category || this.determineCategory(error, errorMessage);

    const notification: ErrorNotification = {
      id: this.generateId(),
      message: this.formatErrorMessage(errorMessage, category),
      severity,
      category,
      timestamp: new Date(),
      context: options.context,
      retryable: options.retryable,
      retryAction: options.retryAction,
    };

    // Ajouter à la liste
    this.notifications.unshift(notification);
    if (this.notifications.length > this.maxNotifications) {
      this.notifications = this.notifications.slice(0, this.maxNotifications);
    }

    // Logger l'erreur
    this.logError(error, notification);

    // Reporter à ErrorHandlerService
    if (error instanceof Error) {
      this.errorHandler.handleError(
        error,
        {
          component: String(options.context?.component || 'Unknown'),
          action: String(options.context?.action || 'Unknown'),
        },
        severity === 'critical' ? 'critical' : severity === 'high' ? 'high' : 'medium'
      );
    }

    // Afficher le toast si pas silencieux
    if (!options.silent && this.toastCallback) {
      this.toastCallback(notification);
    }

    // Émettre un événement personnalisé
    this.emitErrorEvent(notification);
  }

  /**
   * Gère une opération asynchrone avec gestion d'erreur automatique
   */
  public async handleAsync<T>(
    operation: () => Promise<T>,
    options: {
      errorMessage?: string;
      severity?: ErrorSeverity;
      category?: ErrorCategory;
      fallback?: ErrorFallback;
      silent?: boolean;
    } = {}
  ): Promise<T | null> {
    try {
      return await operation();
    } catch (error) {
      const errorMessage = options.errorMessage || this.extractErrorMessage(error);

      this.notifyError(error, {
        severity: options.severity,
        category: options.category,
        context: { operation: errorMessage },
        silent: options.silent,
      });

      // Si un fallback est fourni, l'afficher
      if (options.fallback) {
        this.showFallback(options.fallback);
      }

      return null;
    }
  }

  /**
   * Gère une opération avec retry automatique
   */
  public async handleWithRetry<T>(
    operation: () => Promise<T>,
    options: {
      maxRetries?: number;
      retryDelay?: number;
      errorMessage?: string;
      severity?: ErrorSeverity;
    } = {}
  ): Promise<T | null> {
    const maxRetries = options.maxRetries || 3;
    const retryDelay = options.retryDelay || 1000;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        return await operation();
      } catch (error) {
        if (attempt === maxRetries) {
          // Dernière tentative échouée
          this.notifyError(error, {
            severity: options.severity || 'high',
            context: {
              operation: options.errorMessage,
              attempts: maxRetries,
            },
          });
          return null;
        }

        // Attendre avant de réessayer
        await new Promise(resolve => setTimeout(resolve, retryDelay * attempt));
        logger.warn(`Tentative ${attempt + 1}/${maxRetries}...`);
      }
    }

    return null;
  }

  /**
   * Obtient les notifications d'erreur récentes
   */
  public getNotifications(limit?: number): ErrorNotification[] {
    return limit ? this.notifications.slice(0, limit) : [...this.notifications];
  }

  /**
   * Efface les notifications
   */
  public clearNotifications(): void {
    this.notifications = [];
  }

  /**
   * Extrait le message d'erreur
   */
  private extractErrorMessage(error: Error | unknown): string {
    if (error instanceof Error) {
      return error.message;
    }
    if (typeof error === 'string') {
      return error;
    }
    return 'Une erreur inattendue est survenue';
  }

  /**
   * Détermine la sévérité de l'erreur
   */
  private determineSeverity(error: Error | unknown): ErrorSeverity {
    if (error instanceof Error) {
      const message = error.message.toLowerCase();

      // Erreurs critiques
      if (message.includes('network') || message.includes('fetch') || message.includes('timeout')) {
        return 'critical';
      }

      // Erreurs élevées
      if (
        message.includes('permission') ||
        message.includes('unauthorized') ||
        message.includes('forbidden')
      ) {
        return 'high';
      }

      // Erreurs moyennes
      if (message.includes('validation') || message.includes('invalid')) {
        return 'medium';
      }
    }

    return 'medium';
  }

  /**
   * Détermine la catégorie de l'erreur
   */
  private determineCategory(error: Error | unknown, message: string): ErrorCategory {
    const lowerMessage = message.toLowerCase();

    if (
      lowerMessage.includes('network') ||
      lowerMessage.includes('fetch') ||
      lowerMessage.includes('timeout')
    ) {
      return 'network';
    }
    if (lowerMessage.includes('validation') || lowerMessage.includes('invalid')) {
      return 'validation';
    }
    if (
      lowerMessage.includes('permission') ||
      lowerMessage.includes('unauthorized') ||
      lowerMessage.includes('forbidden')
    ) {
      return 'permission';
    }
    if (lowerMessage.includes('user') || lowerMessage.includes('input')) {
      return 'user';
    }

    return 'unknown';
  }

  /**
   * Formate le message d'erreur selon la catégorie
   */
  private formatErrorMessage(message: string, category: ErrorCategory): string {
    const categoryMessages: Record<ErrorCategory, string> = {
      network: 'Problème de connexion',
      validation: 'Données invalides',
      permission: 'Accès refusé',
      system: 'Erreur système',
      user: 'Erreur utilisateur',
      unknown: 'Erreur inconnue',
    };

    const categoryPrefix = categoryMessages[category] || categoryMessages.unknown;

    // Si le message est déjà formaté, le retourner tel quel
    if (message.startsWith(categoryPrefix)) {
      return message;
    }

    return `${categoryPrefix}: ${message}`;
  }

  /**
   * Log l'erreur
   */
  private logError(error: Error | unknown, notification: ErrorNotification): void {
    const logMessage = `[${notification.category.toUpperCase()}] ${notification.message}`;

    switch (notification.severity) {
      case 'critical':
        logger.fatal(logMessage, { error, notification });
        break;
      case 'high':
        logger.error(logMessage, { error, notification });
        break;
      case 'medium':
        logger.warn(logMessage, { error, notification });
        break;
      default:
        logger.info(logMessage, { error, notification });
    }
  }

  /**
   * Émet un événement personnalisé pour les erreurs
   */
  private emitErrorEvent(notification: ErrorNotification): void {
    const event = new CustomEvent('app-error', {
      detail: {
        type:
          notification.severity === 'critical' || notification.severity === 'high'
            ? 'error'
            : 'warning',
        message: notification.message,
        notification,
      },
    });
    document.dispatchEvent(event);
  }

  /**
   * Affiche un fallback pour les erreurs critiques
   */
  private showFallback(fallback: ErrorFallback): void {
    // Créer un modal de fallback
    const modal = document.createElement('div');
    modal.className = 'error-fallback-modal';
    modal.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.5);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 10000;
    `;

    const content = document.createElement('div');
    content.style.cssText = `
      background: white;
      padding: 24px;
      border-radius: 8px;
      max-width: 400px;
      text-align: center;
    `;

    content.innerHTML = `
      <div style="font-size: 48px; margin-bottom: 16px;">⚠️</div>
      <h3 style="margin-bottom: 8px; color: #991b1b;">Erreur</h3>
      <p style="margin-bottom: 16px; color: #666;">${fallback.message}</p>
      ${
        fallback.action
          ? `
        <button id="error-fallback-action" style="
          background: #dc2626;
          color: white;
          border: none;
          padding: 8px 16px;
          border-radius: 6px;
          cursor: pointer;
          margin-right: 8px;
        ">${fallback.action.label}</button>
      `
          : ''
      }
      <button id="error-fallback-close" style="
        background: #6b7280;
        color: white;
        border: none;
        padding: 8px 16px;
        border-radius: 6px;
        cursor: pointer;
      ">Fermer</button>
    `;

    modal.appendChild(content);
    document.body.appendChild(modal);

    // Gérer les actions
    const closeBtn = content.querySelector('#error-fallback-close');
    closeBtn?.addEventListener('click', () => {
      document.body.removeChild(modal);
    });

    if (fallback.action) {
      const actionBtn = content.querySelector('#error-fallback-action');
      actionBtn?.addEventListener('click', () => {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        fallback.action!.handler();
        document.body.removeChild(modal);
      });
    }
  }

  /**
   * Génère un ID unique
   */
  private generateId(): string {
    return `error-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }
}
